<template>
  <ValidationObserver class="tenancyDepositReleaseLeabank" v-slot="{ invalid }">
    <h4>
      {{ translate('tenancy.deposit.release.leabank.header.title') }}
    </h4>

    <p class="mt-4">
      {{ translate('tenancy.deposit.release.leabank.header.text') }}
    </p>

    <hl-RadioButton
      :value="releaseType"
      radioValue="full"
      :label="translate('tenancy.deposit.release.leabank.radio.full.label')"
      class="ml-0"
      @input="setFullReleaseType"
    />

    <hl-RadioButton
      v-model="releaseType"
      radioValue="split"
      :label="translate('tenancy.deposit.release.leabank.radio.split.label')"
      class="ml-0"
    />

    <div v-if="releaseType === 'full'" class="tenancyDepositReleaseLeabank__info mt-4">
      <div></div>
      <span>
        {{ translate('tenancy.deposit.release.leabank.full.info.text') }}
      </span>
    </div>

    <div v-if="releaseType === 'split'" class="tenancyDepositReleaseLeabank__split mt-5">
      <div>
        <ValidationProvider
          :name="translate('tenancy.deposit.release.leabank.landlordSplitAmount.name')"
          :rules="`required|min_value:1|max_value:${landlordMaxAmount}`"
          tag="div"
          v-slot="{ errors }"
        >
          <strong>
            {{ translate('tenancy.deposit.release.leabank.landlordSplitAmount.title') }}
          </strong>
          <hl-Input type="number" v-model="landlordSplitAmount" :error="errors[0]" class="mt-2" />
        </ValidationProvider>

        <ValidationProvider rules="no-valid-account-number" tag="div" v-slot="{ errors }" class="mt-3">
          <strong>
            {{ translate('tenancy.deposit.release.leabank.landlordBankAccountNumber.title') }}
          </strong>
          <hl-Input
            type="text"
            v-model="landlordBankAccountNumber"
            v-mask="'####.##.#####'"
            :error="errors[0]"
            class="mt-2"
          />
        </ValidationProvider>
      </div>

      <div>
        <strong>
          {{ translate('tenancy.deposit.release.leabank.tenantAmount.title') }}
        </strong>
        <hl-Input type="number" :value="tenantAmount" disabled class="mt-2" />
      </div>
    </div>

    <hl-Button
      class="mt-4"
      :disabled="invalid || isTerminating || isButtonDisabled"
      @click="initiateDepositTermination"
    >
      <div v-if="isTerminating" class="d-flex">
        <hl-Loader variant="white" size="sm" class="mr-2" />
        {{ translate('tenancy.deposit.release.leabank.buttonLoading') }}
      </div>
      <template v-else>
        {{ translate('tenancy.deposit.release.leabank.button') }}
      </template>
    </hl-Button>

    <p v-if="error" class="mt-4 text-danger">
      {{ error }}
    </p>
  </ValidationObserver>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import RadioButton from '@modules/husleie-framework/src/components/forms/inputs/radio-buttons/RadioButton.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Loader from '@modules/husleie-framework/src/components/loaders/LoaderDots.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import { TenancyApi } from '@modules/husleieapi2';
import { InitiateReleaseError } from '@modules/husleie-framework/src/types/NODepositAccount.entity';

export default Vue.extend({
  name: 'TenancyDepositReleaseLeabank',
  mixins: [i18nCommon],
  data() {
    return {
      releaseType: undefined,
      landlordSplitAmount: 0,
      landlordBankAccountNumber: '',
      isTerminating: false,
      depAccount: undefined,
      error: undefined,
    };
  },
  props: {
    depId: {
      type: String,
      required: true,
    },
    landlordId: {
      type: String,
      required: true,
    },
    tenancyId: {
      type: String,
      required: true,
    },
    rentalObjectId: {
      type: String,
      required: true,
    },
  },
  components: {
    'hl-RadioButton': RadioButton,
    'hl-Input': Input,
    'hl-Button': Button,
    'hl-Loader': Loader,
  },
  computed: {
    ...mapGetters('tenancy', ['getTenancyDeposit']),

    landlordMaxAmount() {
      return parseInt(Math.min(this.depAccount.amount, this.depAccount.meta.agreedAmount));
    },
    tenantAmount() {
      return this.landlordMaxAmount - parseInt(this.landlordSplitAmount);
    },
    isButtonDisabled() {
      if (this.releaseType === 'full') {
        return false;
      }

      if (this.releaseType === 'split' && this.landlordSplitAmount && this.landlordBankAccountNumber) {
        return false;
      }

      return true;
    },
    translationWrongAccountNumber() {
      return this.translate('tenancy.deposit.wrong account number');
    },
    translationCanNotSend() {
      return this.translate(
        'tenancy.deposit.could not send to tenant to close deposit account{dot} Please contact support'
      );
    },
  },
  methods: {
    ...mapActions('tenancy', ['fetchTenancyDeposits']),

    async initiateDepositTermination() {
      this.isTerminating = true;
      try {
        const api = new TenancyApi();
        await api.initiateLandlordTenancyDepositRelease(this.landlordId, this.tenancyId, this.depId, {
          body: {
            splitAmount: parseInt(this.landlordSplitAmount),
            landlordBankAccountNumber: this.landlordBankAccountNumber,
          },
        });
        this.$router.push({
          name: 'tenancy_deposit',
          params: {
            landlordId: this.landlordId,
            tenancyId: this.tenancyId,
          },
        });
      } catch (error) {
        const { message } = error.response.data.errors[0];

        if (message === InitiateReleaseError.WRONG_BANK_ACCOUNT_NUMBER) {
          this.error = this.translationWrongAccountNumber;
        } else {
          this.error = this.translationCanNotSend;
        }
        throw new Error(`Failed to initiate deposit release (leabank): ${this.error}`);
      } finally {
        this.isTerminating = false;
      }
    },
    setFullReleaseType() {
      this.releaseType = 'full';
      this.landlordSplitAmount = 0;
    },
  },
  async created() {
    try {
      await this.fetchTenancyDeposits({
        landlordId: this.landlordId,
        tenancyId: this.tenancyId,
      });
      this.depAccount = this.getTenancyDeposit(this.depId);
    } catch {
      throw new Error('Failed to fetch tenancy deposits');
    }
  },
});
</script>

<style lang="scss" scoped>
.tenancyDepositReleaseLeabank__info {
  display: flex;
  gap: 0rem 0.8rem;
}
.tenancyDepositReleaseLeabank__info div {
  width: 4px;
  min-height: 100%;
  background: #eed202;
}
.tenancyDepositReleaseLeabank__info span {
  width: 600px;
}
.tenancyDepositReleaseLeabank__split {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem 3rem;
}
.tenancyDepositReleaseLeabank__split strong {
  font-size: 1rem;
}
</style>
